import React, { useState, useCallback, useEffect } from 'react';
import { Table, Empty, Confirmation, Button } from '@core/uilibrary';
import '../css/contentMainPage.css';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { status } from './contentPageData';
import { DocConfirmationIcon, EditIcon, WarningConfirmationIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter';
import SelectField from '../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField';
import SearchField from '../../../../../component-controller-mod/component-serv/input-fs-cp/search-field-op/ts/searchField';
import ActionButton, { ActionButtonItem } from '../../../../../component-controller-mod/component-serv/button-fs-cp/action-button-op/ts/actionButton';
import IconActionButton from '../../../../../component-controller-mod/component-serv/button-fs-cp/icon-button-op/ts/iconActionButton';
import StatusBadge, { BadgeVariant } from '../../../../../component-controller-mod/component-serv/badge-fs-cp/dynamic-badge-op/ts/statusBadge';
import ApiGet from '../../../../../api-controller-mod/api-serv/get-api/getApi';
import ApiPost from '../../../../../api-controller-mod/api-serv/post-api/postApi';
import ApiPut from '../../../../../api-controller-mod/api-serv/put-api/putApi';
import { API_ENDPOINTS } from '../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import { useToast } from '../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { useGlobalPopupProvider } from '../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { groupPermissionAccessContent } from '../../../../../context-controller-mod/context-serv/permission-provider/permissionExporter';
import CancelButton from '../../../../../component-controller-mod/component-serv/button-fs-cp/cancel-button-op/ts/cancelButton';


// ─── Domain types ─────────────────────────────────────────────────────────────

export interface ContentRow {
  id: string;
  section: string;
  title: string;
  contentType: string;
  author: string;
  status: 'Published' | 'Draft' | 'Unpublish';
  contentId: string;   // numeric id from list API — used for new-draft call
  contentUuid: string;   // uuid string — used for status update PUT call
  // stored from fetch — needed for status update API call
  versionUuid: string;
  sectionApiKey: string;   // e.g. 'heroSection', 'positionSection' — the path key
  pageSlug: string;   // e.g. 'career', 'company' — first segment of path
}

interface ApiSection {
  key: string;
  type?: string;
  title?: string;
  headline?: string;
  author?: string;
  status?: string;
  visible: boolean;
  content?: Array<Record<string, { title?: string; label?: string } | undefined>>;
  [key: string]: unknown;
}

interface ApiSession {
  [key: string]: ApiSection | undefined;
}

interface ApiHeaderPage {
  key?: string;
  title?: string;
  author?: string;
  status?: string;
  visible?: boolean;
  cta?: Record<string, unknown>;
  [key: string]: unknown;
}

interface ApiFooterPage {
  key?: string;
  title?: string;
  visible?: boolean;
  cta?: Array<{
    layout?: {
      key?: string;
      visible?: boolean;
      content?: Array<Record<string, ApiSection>>;
    };
  }>;
  [key: string]: unknown;
}

interface ApiGenericPage {
  key?: string;
  title?: string;
  visible?: boolean;
  cta?: ApiSession[];
  [key: string]: unknown;
}

interface ApiPageData {
  header?: ApiHeaderPage;
  footer?: ApiFooterPage;
  [pageKey: string]: ApiHeaderPage | ApiFooterPage | ApiGenericPage | undefined;
}

interface SlugResponse {
  success: boolean;
  data: {
    contentId?: number | string;
    contentUuid?: string;
    contentVersionUuid?: string;   // ← needed for versionUuid
    title?: string;
    status?: string;
    session?: ApiSession[] | ApiSection;
    data?: ApiPageData;
  };
}

// ─── New-draft API response ───────────────────────────────────────────────────

interface NewDraftResponse {
  success: boolean;
  requestId: string;
  errorMessage?: string;
  error?: string;
  data: {
    contentId: number;
    contentVersionUuid: string;
    contentUuid: string;
    version: number;
    data: Record<string, unknown>;
    summary: null | string;
    createdBy: string;
    isPublished: boolean;
    status: string;
    createdAt: string;
  };
}

// Existing draft returned by GET /api/v1/content/version/draft?contentUuid=...
interface ExistingDraftResponse {
  success: boolean;
  data: {
    contentVersionUuid: string;
    contentUuid: string;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SECTION_KEY_MAP: Record<string, string> = {
  heroSection: 'Hero',
  contactSection: 'Contact',
  productSection: 'Product',
  joinusSection: 'Join Us',
  ourValueSection: 'Our Value',
  ourmissionSection: 'Our Mission',
  differentiatorSection: 'Differentiator',
  footerLeft: 'Footer Left',
  footerRight: 'Footer Right',
  headerSection: 'Header Section',
};

function mapApiStatus(apiStatus?: string): ContentRow['status'] {
  const s = (apiStatus ?? '').toUpperCase();
  if (s === 'PUBLISHED') return 'Published';
  if (s === 'DRAFT') return 'Draft';
  return 'Unpublish';
}

function statusToBadgeVariant(rowStatus: ContentRow['status']): BadgeVariant {
  switch (rowStatus) {
    case 'Published': return 'published';
    case 'Draft': return 'draft';
    default: return 'unpublished';
  }
}

function extractTitle(section: ApiSection): string {
  if (section.title) return section.title;
  if (typeof section.headline === 'string' && section.headline) return section.headline;
  if (Array.isArray(section.content) && section.content.length > 0) {
    const block = section.content[0];
    for (const subKey of Object.keys(block)) {
      const sub = block[subKey];
      if (sub && typeof sub === 'object' && 'title' in sub && sub.title) {
        return sub.title as string;
      }
    }
  }
  return '';
}

function pushRow(
  rows: ContentRow[],
  key: string,
  section: ApiSection,
  contentType: string = 'Section',
  contentId: string = '',
  contentUuid: string = '',
  versionUuid: string = '',
  pageSlug: string = '',
  fallbackStatus?: string,
): void {
  rows.push({
    id: key,
    section: SECTION_KEY_MAP[key] ?? key,
    title: extractTitle(section),
    contentType,
    author: section.author ?? 'Admin',
    status: mapApiStatus(section.status ?? fallbackStatus),
    contentId,
    contentUuid,
    versionUuid,
    sectionApiKey: key,
    pageSlug,
  });
}

function parseHeaderPage(
  page: ApiHeaderPage,
  responseTitle?: string,
  responseStatus?: string,
  contentId?: string,
  contentUuid?: string,
  versionUuid?: string,
  pageSlug?: string,
): ContentRow[] {
  const label = responseTitle ?? page.title ?? 'Header';
  return [{
    id: 'header',
    section: label,
    title: label,
    contentType: 'Navigation',
    author: page.author ?? 'Admin',
    status: mapApiStatus(responseStatus ?? page.status),
    contentId: contentId ?? '',
    contentUuid: contentUuid ?? '',
    versionUuid: versionUuid ?? '',
    sectionApiKey: 'header',
    pageSlug: pageSlug ?? '',
  }];
}

function parseFooterPage(
  page: ApiFooterPage,
  responseTitle?: string,
  responseStatus?: string,
  contentId?: string,
  contentUuid?: string,
  versionUuid?: string,
  pageSlug?: string,
): ContentRow[] {
  const label = responseTitle ?? page.title ?? 'Footer';
  return [{
    id: 'footer',
    section: label,
    title: label,
    contentType: 'Footer',
    author: 'Admin',
    status: mapApiStatus(responseStatus),
    contentId: contentId ?? '',
    contentUuid: contentUuid ?? '',
    versionUuid: versionUuid ?? '',
    sectionApiKey: 'footer',
    pageSlug: pageSlug ?? '',
  }];
}

function parseGenericPage(
  page: ApiGenericPage,
  contentId?: string,
  contentUuid?: string,
  versionUuid?: string,
  pageSlug?: string,
  responseStatus?: string,
): ContentRow[] {
  const rows: ContentRow[] = [];
  const ctaArr = page.cta;
  if (!Array.isArray(ctaArr) || !ctaArr.length) return rows;

  Object.entries(ctaArr[0]).forEach(([sectionKey, section]) => {
    if (!section) return;
    pushRow(
      rows,
      sectionKey,
      section as ApiSection,
      'Section',
      contentId ?? '',
      contentUuid ?? '',
      versionUuid ?? '',
      pageSlug ?? '',
      responseStatus,
    );
  });

  return rows;
}

function mapPageDataToRows(
  pageData: ApiPageData,
  responseTitle?: string,
  responseStatus?: string,
  contentId?: string,
  contentUuid?: string,
  versionUuid?: string,
): ContentRow[] {
  const rows: ContentRow[] = [];

  for (const pageKey of Object.keys(pageData)) {
    const page = pageData[pageKey];
    if (!page) continue;

    if (pageKey === 'header') {
      rows.push(...parseHeaderPage(
        page as ApiHeaderPage, responseTitle, responseStatus, contentId, contentUuid, versionUuid, pageKey,
      ));
    } else if (pageKey === 'footer') {
      rows.push(...parseFooterPage(
        page as ApiFooterPage, responseTitle, responseStatus, contentId, contentUuid, versionUuid, pageKey,
      ));
    } else {
      rows.push(...parseGenericPage(
        page as ApiGenericPage, contentId, contentUuid, versionUuid, pageKey, responseStatus,
      ));
    }
  }

  return rows;
}

function parseSlugResponse(
  axiosRes: { data: SlugResponse },
  slug: string,
  slugLabel: string,
): ContentRow[] {
  const payload = axiosRes?.data;
  if (!payload?.success) return [];

  const inner = payload.data;
  if (!inner) return [];

  // contentId (numeric) → used for new-draft API call
  // contentUuid (string)  → used for status-update PUT call
  const contentId = String(inner.contentId ?? '');
  const contentUuid = String(inner.contentUuid ?? inner.contentId ?? '');
  const versionUuid = inner.contentVersionUuid ?? '';

  if (inner.data && typeof inner.data === 'object' && !Array.isArray(inner.data)) {
    return mapPageDataToRows(
      inner.data as ApiPageData,
      inner.title,
      inner.status,
      contentId,
      contentUuid,
      versionUuid,
    );
  }

  if (inner.session !== undefined) {
    const session = inner.session as ApiSession[] | ApiSection;

    if (!Array.isArray(session)) {
      return [{
        id: slugLabel.toLowerCase(),
        section: slugLabel,
        title: (session as any)?.content?.label ?? (session as ApiSection).key ?? '',
        contentType: 'Navigation item',
        author: (session as ApiSection).author ?? 'Admin',
        status: mapApiStatus((session as ApiSection).status ?? inner.status),
        contentId,
        contentUuid,
        versionUuid,
        sectionApiKey: slugLabel.toLowerCase(),
        pageSlug: slug,
      }];
    }

    if (!session.length) return [];
    const first = (session as ApiSession[])[0];
    const firstKeys = Object.keys(first);

    if (firstKeys.length === 1) {
      const topSection = first[firstKeys[0]] as ApiSection | undefined;
      if (topSection && Array.isArray(topSection.content)) {
        let title = '';
        for (const block of topSection.content as any[]) {
          for (const subVal of Object.values(block)) {
            const t = extractTitle(subVal as ApiSection);
            if (t) { title = t; break; }
          }
          if (title) break;
        }
        return [{
          id: slugLabel.toLowerCase(),
          section: slugLabel,
          title,
          contentType: 'Footer',
          author: 'Admin',
          status: mapApiStatus(inner.status),
          contentId,
          contentUuid,
          versionUuid,
          sectionApiKey: firstKeys[0],
          pageSlug: slug,
        }];
      }
    }

    const rows: ContentRow[] = [];
    Object.entries(first).forEach(([key, section]) => {
      if (!section) return;
      pushRow(
        rows,
        key,
        section as ApiSection,
        'Section',
        contentId,
        contentUuid,
        versionUuid,
        slug,
        inner.status,
      );
    });
    return rows;
  }

  return [];
}

// ─── Slug routing ─────────────────────────────────────────────────────────────

const SLUG_MAP: Record<string, string | string[]> = {
  sale: 'saleContact',
  business: 'businessContact',
  'job-detail': 'jobDetail',
  'job-position': 'jobPosition',
  navigation: ['header', 'footer'],
};

function toApiSlugs(contentType: string): string[] {
  const mapped = SLUG_MAP[contentType] ?? contentType;
  return Array.isArray(mapped) ? mapped : [mapped];
}

// ─── Action types ─────────────────────────────────────────────────────────────

interface ActionConfig {
  edit?: boolean;
  showStatusActions?: boolean;
  onView?: (rowId: string) => void;
  onEdit?: (rowId: string) => void;
}

interface ContentMainPageProps {
  actions?: ActionConfig;
}

// ─── Page Component ───────────────────────────────────────────────────────────

const ContentMainPage: React.FC<ContentMainPageProps> = ({
  actions = { edit: true, showStatusActions: true },
}) => {
  const { contentType } = useParams<{ contentType: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rows, setRows] = useState<ContentRow[]>([]);
  const [loading, setLoading] = useState(true);

  const canUpdateContent = groupPermissionAccessContent.update()

  // ── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!contentType) return;

    let cancelled = false;
    setLoading(true);

    const slugs = toApiSlugs(contentType);

    Promise.all(
      slugs.map(slug =>
        ApiGet<SlugResponse>(`${API_ENDPOINTS.GET_CMS_CONTENT}?slug=${slug}`)
      )
    )
      .then((responses) => {
        if (cancelled) return;
        const merged: ContentRow[] = responses.flatMap((res, i) => {
          const slug = slugs[i];
          const slugLabel = slug.charAt(0).toUpperCase() + slug.slice(1);
          return parseSlugResponse(res as { data: SlugResponse }, slug, slugLabel);
        });
        setRows(merged);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[ContentMainPage] fetch failed:', err);
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [contentType]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const closeActionMenusSoft = useCallback(() => {
    if (typeof window === 'undefined') return;
    requestAnimationFrame(() => {
      const active = document.activeElement as HTMLElement | null;
      if (active && typeof active.blur === 'function') active.blur();
    });
  }, []);

  // ── Status update ─────────────────────────────────────────────────────────
  const performStatusUpdate = useCallback(async (
    row: ContentRow,
    nextStatus: 'PUBLISHED' | 'UNPUBLISHED',
  ) => {
    const userId = Cookies.get('userUuid') ?? '';

    try {
      await ApiPut(
        `/api/v1/content/update?contentUuid=${row.contentUuid}&userId=${userId}&versionUuid=${row.versionUuid}`,
        {
          type: 'page',
          title: row.title,
          version: 1,
          updates: [
            {
              path: [row.pageSlug, 'cta', 0, row.sectionApiKey, 'status'],
              value: nextStatus,
            },
          ],
        },
      );

      // Optimistically update the row status in local state
      setRows(prev => prev.map(r =>
        r.id === row.id
          ? { ...r, status: nextStatus === 'PUBLISHED' ? 'Published' : 'Unpublish' }
          : r
      ));

      showToast({
        status: 'success',
        title: 'Complete !',
        description: `Section was ${nextStatus === 'PUBLISHED' ? 'published' : 'unpublished'} successfully.`,
      });
    } catch (err) {
      console.error('[ContentMainPage] status update failed:', err);
      showToast({ status: 'error', title: 'Uncomplete !', description: 'Status update failed.' });
    } finally {
      closeActionMenusSoft();
    }
  }, [showToast, closeActionMenusSoft]);

  const handleStatusUpdate = useCallback((
    row: ContentRow,
    nextStatus: 'PUBLISHED' | 'UNPUBLISHED',
  ) => {
    const actionLabel = nextStatus === 'PUBLISHED' ? 'Publish' : 'Unpublish';

    openConfirm(
      <Confirmation
        visible
        title={`${actionLabel} section`}
        description={`Are you sure you want to ${actionLabel.toLowerCase()} the "${row.section}" section? This will update the live content.`}
        status="warning"
        icon={<DocConfirmationIcon />}
        onClose={closeConfirm}
        buttons={
          <>
            <Button variant="ghost" size="md" value="Cancel" type="button" onClick={closeConfirm} />
            <Button variant="primary" size="md" value={actionLabel} type="button"
              onClick={() => { closeConfirm(); performStatusUpdate(row, nextStatus); }}
            />
          </>
        }
      />
    );
  }, [openConfirm, closeConfirm, performStatusUpdate]);

  // ── Edit handler — calls new-draft, embeds UUIDs in URL ─────────────────
  // contentId  (numeric string from GET_CMS_CONTENT) → sent to new-draft API
  // contentUuid + contentVersionUuid from new-draft response → embedded in URL
  // If a DRAFT already exists the API returns a 500 with the error message
  // "Cannot create a new draft. Version N must be PUBLISHED first".
  // In that case we fetch the existing draft version and proceed with it.
  const handleEdit = useCallback(async (rowId: string) => {
    const row = rows.find(r => r.id === rowId);
    // Use numeric contentId for the new-draft call (what the API expects)
    const contentId = row?.contentId ?? row?.contentUuid ?? '';
    const userUuid = Cookies.get('userUuid') ?? '';

    const goToEdit = (uuid: string, versionUuid: string) => {
      navigate(`/content/${contentType}/${uuid}/${versionUuid}/${rowId}/edit`);
    };

    try {
      const res = await ApiPost<NewDraftResponse>('/api/v1/content/new-draft', {
        userUuid,
        contentUuid: contentId,
      });

      // API may return 200 but with success: false when a draft already exists
      if (!res?.data?.success) {
        const msg = res?.data?.error ?? res?.data?.errorMessage ?? '';
        if (msg.toLowerCase().includes('draft')) {
          // Fall through to fetch the existing draft below
          throw new Error('DRAFT_EXISTS');
        }
        showToast({ status: 'error', title: 'Error', description: msg || 'Could not create draft.' });
        return;
      }

      // Always use the UUIDs returned by new-draft — never the row's contentId
      const draftContentUuid = res.data.data.contentUuid ?? '';
      const draftContentVersionUuid = res.data.data.contentVersionUuid ?? '';

      // Set cookie so edit pages can still read it as a fallback
      if (draftContentVersionUuid) {
        Cookies.set('contentVersionUuid', draftContentVersionUuid);
      }

      goToEdit(draftContentUuid, draftContentVersionUuid);
    } catch (err: any) {
      const isDraftExists =
        err?.message === 'DRAFT_EXISTS' ||
        (err?.response?.data?.error ?? '').toLowerCase().includes('draft') ||
        (err?.response?.data?.errorMessage ?? '').toLowerCase().includes('draft');

      if (isDraftExists) {
        // Fetch the existing draft version and navigate with it
        try {
          const draftRes = await ApiGet<ExistingDraftResponse>(
            `${API_ENDPOINTS.FIND_CONTENT_BY_VERSION}?contentUuid=${contentId}&status=DRAFT`
          );
          const existing = draftRes?.data?.data;
          if (existing?.contentVersionUuid) {
            Cookies.set('contentVersionUuid', existing.contentVersionUuid);
            goToEdit(existing.contentUuid ?? contentId, existing.contentVersionUuid);
            return;
          }
        } catch (fetchErr) {
          console.warn('[ContentMainPage] fetch existing draft failed:', fetchErr);
        }
        showToast({ status: 'warning', title: 'Draft exists', description: 'A draft already exists for this section. Please publish or discard it first.' });
        return;
      }

      console.warn('[ContentMainPage] new-draft failed:', err);
      showToast({ status: 'error', title: 'Error', description: 'Could not create draft. Please try again.' });
    }
  }, [navigate, contentType, rows, showToast]);

  const handleView = useCallback((rowId: string) => {
    actions.onView?.(rowId);
  }, [actions]);

  // ── Action dropdown items — Publish / Unpublish ───────────────────────────
  // Only show the opposite action: Published rows → Unpublish only
  //                                Unpublished/Draft rows → Publish only
  const buildActionItems = useCallback((row: ContentRow): ActionButtonItem[] => {
    if (!actions.showStatusActions) return [];
    const isPublished = row.status === 'Published';
    return isPublished
      ? [
        {
          id: `${row.id}-unpublish`,
          label: 'Unpublished',
          icon: '',
          onClick: () => handleStatusUpdate(row, 'UNPUBLISHED'),
        },
      ]
      : [
        {
          id: `${row.id}-publish`,
          label: 'Published',
          icon: '',
          onClick: () => handleStatusUpdate(row, 'PUBLISHED'),
        },
      ];
  }, [actions.showStatusActions, handleStatusUpdate]);

  const renderActions = useCallback((row: ContentRow) => {
    const actionItems = buildActionItems(row);
    return (
      <div className="scl--cms-pages-content-actions">
        {canUpdateContent &&
          <>
            {actions.edit && (
              <IconActionButton
                icon={<EditIcon />}
                onClick={() => handleEdit(row.id)}
                ariaLabel="Edit"
                title="Edit"
                className="scl--cms-components-iconActionButton--edit"
              />
            )}
            {actions.showStatusActions && actionItems.length > 0 && (
              <div className="scl--cms-pages-content-actionDropdown">
                <ActionButton items={actionItems} iconVariant="horizontal" />
              </div>
            )}
          </>
        }
      </div>
    );
  }, [actions, buildActionItems, handleEdit]);

  // ── Filter ───────────────────────────────────────────────────────────────
  const filteredData = rows.filter(row => {
    const matchesSearch = row.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      !statusFilter ||
      statusFilter === 'all-status' ||
      row.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  // ── Table ────────────────────────────────────────────────────────────────
  const tableData = filteredData.map((row, index) => ({
    id: row.id,
    ID: String(index + 1).padStart(2, '0'),
    Section: row.section,
    Title: row.title,
    'Content Type': row.contentType,
    Author: row.author,
    Status: <StatusBadge variant={statusToBadgeVariant(row.status)} />,
    Action: renderActions(row),
  }));

  const columns = [
    'ID',
    'Section',
    'Title',
    'Content Type',
    'Author',
    'Status',
    ...(canUpdateContent ? ['Action'] : []),
  ];
  const hasActiveFilters = Boolean(
  (statusFilter && statusFilter !== 'all-status') || searchQuery
);

const handleClearFilters = useCallback(() => {
  setStatusFilter('');
  setSearchQuery('');
}, []);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="scl--cms-pages-content">

      <div className="scl--cms-pages-content-header">
        <div className="scl--cms-pages-content-headerContainer">
          <h1 className="scl--cms-pages-content-title">Content</h1>
          <p className="scl--cms-pages-content-description">
            Controls how {contentType} content is created, reviewed, published, and managed.
          </p>
        </div>
      </div>

      <div className="scl--cms-pages-content-filters">
        <div className="scl--cms-pages-content-filtersSelectField">
          <SelectField
            name="status"
            placeholder="All status"
            label="Status"
            value={statusFilter}
            options={status}
            onChange={value => setStatusFilter(String(value))}
          />
        </div>
        <div className="scl--cms-pages-content-filtersSearch">
          <SearchField
            name="search"
            placeholder="Search"
            label="Search"
            value={searchQuery}
            onChange={value => setSearchQuery(value)}
          />
        </div>
        {hasActiveFilters && (
          <CancelButton onClick={handleClearFilters} aria-label="Clear all filters" />
        )}
      </div>

      {loading ? (
        <div className="scl--cms-pages-content-emptyWrapper">
          <span>Loading...</span>
        </div>
      ) : filteredData.length > 0 ? (
        <div className="scl--cms-pages-content-tableWrapper">
          <Table
            columns={columns}
            data={tableData}
            showCheckbox={false}
            showId={false}
            showPagination
            rowsPerPageOptions={[5, 10, 20]}
            defaultRowsPerPage={10}
            backgroundColor="transparent"
            selectColumnWidth={49}
            columnWidths={{
              ID: '7%',
              Section: '15%',
              Title: '20%',
              'Content Type': '15%',
              Author: '15%',
              Status: '17%',
              Action: '11%',
            }}
          />
        </div>
      ) : (
        <div className="scl--cms-pages-content-emptyWrapper">
          <Empty
            title="No Data yet"
            description="There's nothing here yet. Start by adding new data to see it listed here."
          />
        </div>
      )}

    </div>
  );
};

export default ContentMainPage;